// Where the storefront really sends an order to, and whether that POS exists.
//
// The Verwerking screen used to show the raw stored values ('00' as "0200",
// '' as "None"), which hid what actually happens and let invalid values sit
// unnoticed. These helpers mirror the storefront (server/v1):
//   - pickup registration  OrderController::handleBasicData (PICKUP branch)
//   - pickup printing      OrderController::processReservationPickup
//   - delivery printing    OrderController::receiptPrintPosId() + processReservationPayed
//   - every print message  API::PrintMessage()
// so each POS field can show its real target and be flagged when that target
// is malformed or not one of the customer's registers. Keep them in sync with
// those files.

const str = (v) => (v == null ? '' : String(v).trim());
const isSet = (v) => { const s = str(v); return s !== '' && s !== '00'; };
const isPosCode = (code) => /^\d{4}$/.test(code);

// Store codes are 2 characters ('01'); the stores list can hold them unpadded.
export const storeCode = (store) => str(store && store.id).padStart(2, '0');

// API::PrintMessage(): a full 4-digit code is used as-is, anything shorter is
// a register of this store (zero-padded to 2 digits).
export const printTarget = (store, v) => {
    const s = str(v);
    if (!s) return `${store}01`;
    return s.length >= 4 ? s : store + s.padStart(2, '0');
};

// Pickup registration: '00' reads as register 01 of the store, a value shorter
// than 4 characters gets the store code in front (NOT padded -- '8' becomes
// the invalid '048'), an empty value falls back to the processing POS.
export const pickupRegisterTarget = (store, posId, processPos) => {
    const raw = str(posId);
    if (raw === '') return { code: str(processPos), via: 'webshop' };
    const v = raw === '00' ? '01' : raw;
    return { code: v.length < 4 ? store + v : v, via: raw === '00' ? 'default' : 'explicit' };
};

// Pickup printing: pos_id_print, else pos_id, else PROCESS_TICKET_POS_ID
// ('' and '00' count as not set).
export const pickupPrintTarget = (store, posIdPrint, posId, ticketPos) => {
    if (isSet(posIdPrint)) return { code: printTarget(store, posIdPrint), via: 'explicit' };
    if (isSet(posId))      return { code: printTarget(store, posId), via: 'register' };
    return { code: printTarget(store, ticketPos), via: 'ticket' };
};

// Deliveries are not tied to a store: they are processed in PROCESS_STORE, or
// -- when deliveries become receipts -- in the store of the processing POS.
export const deliveryStore = (config) => {
    const module = str(config && config.apiModule).toUpperCase();
    const process = str(config && config.receiptPos && config.receiptPos.process);
    if ((module === 'RECEIPT_BOTH' || module === 'RECEIPT_SHIPMENT') && process.length >= 2) {
        return process.slice(0, 2);
    }
    return str(config && config.warehouse).padStart(2, '0');
};

// Delivery printing: the last 2 digits of PROCESS_RECEIPT_POS_ID_RECEIPT inside
// the delivery store; empty (or missing) -> PROCESS_TICKET_POS_ID.
export const deliveryPrintTarget = (config) => {
    const store = deliveryStore(config);
    const raw = str(config && config.receiptPos && config.receiptPos.print);
    const reg = raw.slice(-2);
    if (reg) return { code: store + reg.padStart(2, '0'), via: 'explicit' };
    return { code: printTarget(store, (config && config.ticketPos) || '01'), via: 'ticket' };
};

// The customer's registers as a Set, or null when the list did not load (then
// nothing is flagged as "does not exist" -- no false alarms).
export const knownPosCodes = (posOptions) => (
    Array.isArray(posOptions) && posOptions.length
        ? new Set(posOptions.map(o => String(o.value)))
        : null
);

// Shape of an issue: { level: 'error' | 'warn', reason, code, raw }
const targetIssue = (code, known, raw) => {
    if (!isPosCode(code))            return { level: 'error', reason: 'format',  code, raw };
    if (known && !known.has(code))   return { level: 'error', reason: 'missing', code, raw };
    return null;
};

// Raw value in a store field: 1, 2 or 4 digits (or ''/'00').
const rawFormatIssue = (raw, allowSingleDigit) => {
    const s = str(raw);
    if (s === '' || s === '00') return null;
    const okLen = s.length === 2 || s.length === 4 || (allowSingleDigit && s.length === 1);
    return (/^\d+$/.test(s) && okLen) ? null : { level: 'error', reason: 'format', code: s, raw: s };
};

/** Issue for a store's "Registreren" (pos_id) field, or null. */
export const pickupRegisterIssue = (storeRow, processPos, known) => {
    const store = storeCode(storeRow);
    // pos_id is varchar(2) and never padded by the storefront: '8' -> '048'.
    const fmt = rawFormatIssue(storeRow.posId, false);
    if (fmt) return fmt;
    const t = pickupRegisterTarget(store, storeRow.posId, processPos);
    if (t.via === 'webshop' && !str(processPos)) {
        return { level: 'error', reason: 'notSet', code: '', raw: '' };
    }
    return targetIssue(t.code, known, str(storeRow.posId));
};

/** Issue for a store's "Afdrukken" (pos_id_print) field, or null. */
export const pickupPrintIssue = (storeRow, ticketPos, known) => {
    const store = storeCode(storeRow);
    const fmt = rawFormatIssue(storeRow.posIdPrint, true);
    if (fmt) return fmt;
    const t = pickupPrintTarget(store, storeRow.posIdPrint, storeRow.posId, ticketPos);
    const issue = targetIssue(t.code, known, str(storeRow.posIdPrint));
    if (issue) return issue;
    const raw = str(storeRow.posIdPrint);
    if (raw.length === 4 && raw.slice(0, 2) !== store) {
        return { level: 'warn', reason: 'otherStore', code: raw, raw };
    }
    return null;
};

/** Issue for "Verwerking van het kasticket" (PROCESS_RECEIPT_POS_ID_PROCESS). */
export const receiptProcessIssue = (config, known) => {
    const raw = str(config && config.receiptPos && config.receiptPos.process);
    if (!raw) return { level: 'error', reason: 'notSet', code: '', raw: '' };
    return targetIssue(raw, known, raw);
};

/** Issue for "Afprinten van het kasticket" (PROCESS_RECEIPT_POS_ID_RECEIPT). */
export const receiptPrintIssue = (config, known) => {
    const rp = (config && config.receiptPos) || {};
    const raw = str(rp.print);
    if (!raw) {
        // An empty value is a valid choice ("standard"), a MISSING row is not:
        // since 1.11.4b7 every delivery reads it.
        if (rp.printMissing) return { level: 'error', reason: 'missingRow', code: '', raw: '' };
        return targetIssue(deliveryPrintTarget(config).code, known, raw);
    }
    if (!/^\d{1,4}$/.test(raw) || raw.length === 3) return { level: 'error', reason: 'format', code: raw, raw };
    const t = deliveryPrintTarget(config);
    const issue = targetIssue(t.code, known, raw);
    if (issue) return issue;
    // The storefront keeps only the register: another store's code prints in
    // the delivery store instead.
    if (raw.length === 4 && raw !== t.code) return { level: 'warn', reason: 'deliveryStore', code: t.code, raw };
    return null;
};

/**
 * Error counts per Verwerking sub-tab, for the badge on the tab and the
 * counter on the cards. Warnings are not counted. Stores with pickup switched
 * off (or pickup disabled shop-wide) are skipped: no order goes there.
 */
export const countPosIssues = ({ config, stores, posOptions }) => {
    const known = knownPosCodes(posOptions);
    const isErr = (i) => !!i && i.level === 'error';
    const payments = [receiptProcessIssue(config, known), receiptPrintIssue(config, known)].filter(isErr).length;
    let shipping = 0;
    const pickupOff = config && config.shipping && config.shipping.method === 'shippingOnly';
    if (!pickupOff) {
        const process = config && config.receiptPos && config.receiptPos.process;
        const ticket = (config && config.ticketPos) || '01';
        (stores || []).forEach((s) => {
            if (s.allowPickup === false) return;
            if (isErr(pickupRegisterIssue(s, process, known))) shipping += 1;
            if (isErr(pickupPrintIssue(s, ticket, known)))     shipping += 1;
        });
    }
    return { payments, shipping };
};
